import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { teamAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component
const SortableRow = ({ member, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow 
      ref={setNodeRef} 
      style={style} 
      data-testid={`team-row-${member.id}`}
      className={isDragging ? 'bg-muted/50' : ''}
    >
      <TableCell className="w-10">
        <button
          className="cursor-grab hover:bg-muted p-1 rounded touch-none"
          {...attributes}
          {...listeners}
          data-testid={`drag-handle-${member.id}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </button>
      </TableCell>
      <TableCell>
        <img src={member.image} alt={member.name} className="w-12 h-12 rounded-full object-cover" />
      </TableCell>
      <TableCell className="font-medium">{member.name}</TableCell>
      <TableCell>{member.role}</TableCell>
      <TableCell>{member.email || 'N/A'}</TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end space-x-2">
          <Button variant="ghost" size="icon" onClick={() => onEdit(member)} data-testid={`edit-team-${member.id}`}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(member.id)} className="text-destructive hover:text-destructive" data-testid={`delete-team-${member.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const TeamTab = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({ name: '', role: '', image: '', linkedin: '', email: '' });
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const response = await teamAPI.getAll();
      setTeam(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento del team');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = team.findIndex((item) => item.id === active.id);
      const newIndex = team.findIndex((item) => item.id === over.id);
      
      const newOrder = arrayMove(team, oldIndex, newIndex);
      setTeam(newOrder);

      // Save new order to backend
      setIsSavingOrder(true);
      try {
        await teamAPI.reorder(newOrder.map(m => m.id));
        toast.success('Ordine aggiornato');
      } catch (error) {
        toast.error('Errore nel salvare l\'ordine');
        loadTeam(); // Reload original order
      } finally {
        setIsSavingOrder(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memberForm.image) {
      toast.error('Carica una foto per il membro');
      return;
    }
    try {
      if (editingMember) {
        await teamAPI.update(editingMember.id, memberForm);
        toast.success('Membro aggiornato!');
      } else {
        await teamAPI.create(memberForm);
        toast.success('Membro aggiunto!');
      }
      loadTeam();
      setIsDialogOpen(false);
      setEditingMember(null);
      setMemberForm({ name: '', role: '', image: '', linkedin: '', email: '' });
    } catch (error) {
      toast.error('Errore nel salvare il membro');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo membro?')) return;
    try {
      await teamAPI.delete(id);
      toast.success('Membro eliminato!');
      loadTeam();
    } catch (error) {
      toast.error('Errore nell\'eliminare il membro');
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setMemberForm({
      name: member.name,
      role: member.role,
      image: member.image,
      linkedin: member.linkedin || '',
      email: member.email || ''
    });
    setIsDialogOpen(true);
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div>
            <CardTitle className="text-2xl">Gestione Team</CardTitle>
            <CardDescription>
              Aggiungi, modifica o trascina per riordinare i membri del team
              {isSavingOrder && <span className="ml-2 text-primary">(Salvataggio...)</span>}
            </CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-team-btn"
                onClick={() => { 
                  setEditingMember(null); 
                  setMemberForm({ name: '', role: '', image: '', linkedin: '', email: '' }); 
                }}
              >
                <Plus className="w-4 h-4 mr-2" />Aggiungi Membro
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingMember ? 'Modifica Membro' : 'Nuovo Membro'}</DialogTitle>
                <DialogDescription>{editingMember ? 'Aggiorna le informazioni' : 'Compila i dettagli'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome Completo</Label>
                  <Input 
                    id="name" 
                    data-testid="team-name-input"
                    value={memberForm.name} 
                    onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="role">Ruolo</Label>
                  <Input 
                    id="role" 
                    data-testid="team-role-input"
                    value={memberForm.role} 
                    onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    data-testid="team-email-input"
                    value={memberForm.email} 
                    onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn URL</Label>
                  <Input 
                    id="linkedin" 
                    data-testid="team-linkedin-input"
                    value={memberForm.linkedin} 
                    onChange={(e) => setMemberForm({ ...memberForm, linkedin: e.target.value })} 
                    className="mt-2" 
                    placeholder="https://linkedin.com/in/..." 
                  />
                </div>
                <div>
                  <Label>Foto</Label>
                  <div className="mt-2">
                    <ImageUpload
                      value={memberForm.image}
                      onChange={(url) => setMemberForm({ ...memberForm, image: url })}
                      uploadType="image"
                      label="Carica foto membro"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="submit-team-btn">{editingMember ? 'Aggiorna' : 'Crea'} Membro</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Foto</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : team.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nessun membro</TableCell></TableRow>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={team.map(m => m.id)} strategy={verticalListSortingStrategy}>
                    {team.map((member) => (
                      <SortableRow
                        key={member.id}
                        member={member}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </TableBody>
          </Table>
        </div>
        {team.length > 0 && (
          <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2">
            <GripVertical className="w-4 h-4" />
            Trascina le righe per riordinare i membri del team
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamTab;
